import Vue from '../externals/vue'; // Hack to work-around bundling issue
import moment from 'moment';

// Load correct locale
require('moment/locale/en-au');

//Update calendar format
moment.updateLocale('en-au', {
  calendar: {
    lastDay: '[Yesterday]',
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    lastWeek: 'dddd',
    nextWeek: '[Next] dddd',
    sameElse: 'DD/MM/YY hh:mm a',
  },
});

initFunc();

export default function initFunc() {
  /**
   * The global filter to output standard `countdown` format for Odds.com.au
   */
  Vue.filter('countdown', filterCountdown);
  Vue.filter('sportsCalendar', sportsCalendar);
  Vue.filter('sportsCalendarPunters', sportsCalendarPunters);
  Vue.filter('countdownPunters', filterCountdownPunters);
  Vue.filter('puntersDisplayTime', puntersDisplayTime);
}

/**
 * Uses calendar style formatting for sport event/meeting dates
 * @param date
 * @returns {string}
 */
function sportsCalendar(date) {
  return moment(date).calendar(null, {
    lastDay: '[Yesterday]',
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    lastWeek: 'dddd',
    nextWeek: '[Next] dddd',
    sameElse: 'ddd MMM DD',
  });
}

/**
 * Uses calendar style formatting for sport event/meeting dates for Punters
 * @param date
 * @returns {string}
 */
function sportsCalendarPunters(date) {
  return moment(date).calendar(null, {
    lastDay: '[Yesterday]',
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    lastWeek: 'dddd',
    nextWeek: 'dddd',
    sameElse: 'ddd MMM DD',
  });
}

/**
 * Used to format dispay time on punters website ie: 11:00am
 * @param datetime
 * @returns {string}
 */
function puntersDisplayTime(dateTime) {
  return moment(dateTime).format('HH:mma');
}

/**
 * Callback function of filter `countdown`.
 * Taking care of unix and ISO8601 format conditions.
 *
 * @param {number} timeStamp: starting time of the game
 */
function filterCountdown(timeStamp, isLegacyFormat = true) {
  const typeOfTimeStamp = typeof timeStamp;
  const currentTimeLocal = Date.now();
  // Purpose of if/else condition:
  // Currently there are 2 types of timeStamp value from API (`2018-03-11 09:55:00` and `1517188775217`)
  // this can provide the logic to take care both cases, and output correct time format as required
  if (typeOfTimeStamp === 'string') {
    // Local moment
    const eventStartTimeLocal = moment(timeStamp);
    const isNumber = false;
    // calculating the difference between now and the event starting time (in secs)
    const remainingTime = eventStartTimeLocal.diff(currentTimeLocal) / 1000;
    // return time format
    return fnOutputTimeFormat(remainingTime, eventStartTimeLocal, isNumber, isLegacyFormat);
  } else if (typeOfTimeStamp === 'number') {
    // The condition of timeStamp in this type is `1517188775217`
    // calculating the difference between now and the event starting time (in secs)
    const remainingTime = Math.floor(timeStamp - currentTimeLocal / 1000);
    const isNumber = true;
    // return time format
    return fnOutputTimeFormat(remainingTime, timeStamp, isNumber, isLegacyFormat);
  }
}

/**
 * This function deals with the logic that returns correct time format
 * Typical countdown format would like below examples:
 * 58s | 26m 35s | 14h 32m | 4d 16h | 2w 6d
 *
 * @param {number} remainingTime: Difference between now and game starting time.
 * @param {number} eventStartsAt: Game starting time.
 * @param {boolean} isNumber: If true, it means type of value is Number
 */
function fnOutputTimeFormat(remainingTime, eventStartsAt, isNumber, isLegacyFormat = true) {
  if (!isLegacyFormat) {
    if (remainingTime <= 0) {
      // Game has already started
      return 'ENDED';
    } else if (remainingTime < 60) {
      // less than 60s
      // time format example in this condition: 58s
      const sec = Math.floor(remainingTime);
      return `${sec}s`;
    } else if (remainingTime < 600) {
      // less than 10m
      // time format example in this condition: 15m 58s
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      const sec = Math.floor(remainingTime - min * 60);
      return `${min}m ${sec}s`;
    } else if (remainingTime >= 600 && remainingTime < 3600) {
      // larger than 10m , less than 1hr
      // time format: 45m
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      return `${min}m`;
    } else if (remainingTime > 3600) {
      const now = moment();
      const isCurrentDate = eventStartsAt.isSame(now, 'day');
      if (isCurrentDate) {
        // larger than 1hr, but still at current day
        // time format: 10:30pm
        return moment(eventStartsAt).format('h:mma');
      } else if (!isCurrentDate) {
        // larger than 1hr, but over night
        // time format: Sat 11:12am
        return moment(eventStartsAt).format('ddd h:mma');
      }
    }
  }

  if (isLegacyFormat) {
    if (remainingTime <= 0) {
      // Game has already started
      // show how long ago it was
      return isNumber ? 'ENDED' : moment(eventStartsAt).fromNow();
    } else if (remainingTime < 60) {
      // less than 60s
      // time format examples in this condition: 58s
      const sec = Math.floor(remainingTime);
      return `${sec}s`;
    } else if (remainingTime < 3600) {
      // less than 1hr
      // time format examples in this condition: 26m 35s
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      const sec = Math.floor(remainingTime - min * 60);
      return `${min}m ${sec}s`;
    } else if (remainingTime < 3600 * 24) {
      // less than 1 day
      // time format examples in this condition: 14h 32m
      const hour = Math.floor(remainingTime / 3600);
      const min = Math.floor((remainingTime - hour * 3600) / 60);
      return `${hour}h ${min}m`;
    } else if (remainingTime < 3600 * 24 * 7) {
      // less than 1 week
      // time format examples in this condition: 4d 16h
      const day = Math.floor(remainingTime / (3600 * 24));
      const hour = Math.floor((remainingTime - day * 3600 * 24) / (60 * 60));
      return `${day}d ${hour}h`;
    } else if (remainingTime > 3600 * 24 * 7) {
      // large than 1 week
      // time format examples in this condition: 2w 6d
      const week = Math.floor(remainingTime / (3600 * 24 * 7));
      const day = Math.floor((remainingTime - week * 3600 * 24 * 7) / (60 * 60 * 24));
      return `${week}w ${day}d`;
    }
  }
}

/*
 * Punters countdown filters
 */

/**
 * Callback function of filter `countdown`.
 * Taking care of unix and ISO8601 format conditions.
 *
 * @param {number} timeStamp: starting time of the game
 */
function filterCountdownPunters(timeStamp) {
  const typeOfTimeStamp = typeof timeStamp;
  const currentTimeLocal = Date.now();
  // console.log('timeStamp >>>', timeStamp);

  // Purpose of if/else condition:
  // Currently there are 2 types of timeStamp value from API (`2018-03-11 09:55:00` and `1517188775217`)
  // this can provide the logic to take care both cases, and output correct time format as required
  if (typeOfTimeStamp === 'string') {
    // Local moment
    const eventStartTimeLocal = moment(timeStamp);

    // calculating the difference between now and the event starting time (in secs)
    const remainingTime = eventStartTimeLocal.diff(currentTimeLocal) / 1000;
    // return time format
    return fnOutputTimeFormatPunters(remainingTime, eventStartTimeLocal, false);
  } else if (typeOfTimeStamp === 'number') {
    // The condition of timeStamp in this type is `1517188775217`
    // calculating the difference between now and the event starting time (in secs)
    const remainingTime = Math.floor(timeStamp - currentTimeLocal / 1000);
    // return time format
    return fnOutputTimeFormatPunters(remainingTime, timeStamp, true);
  }
}

/**
 * This function deals with the logic that returns correct time format
 * Typical countdown format would like below examples:
 * 58s | 9m 55s | 26m
 *
 * @param {number} remainingTime: Difference between now and event starting time.
 * @param {boolean} isNumber: If true, it means type of value is Number
 */
function fnOutputTimeFormatPunters(remainingTime, eventStartsAt, isNumber = false) {
  if (!isNumber) {
    if (remainingTime <= 0) {
      // event already started
      // time format example: -5m
      const currentTimeLocal = moment(Date.now());
      const min = currentTimeLocal.diff(eventStartsAt, 'minutes');
      return `-${min}m`;
    } else if (remainingTime < 60) {
      // less than 60s
      // time format example in this condition: 58s
      const sec = Math.floor(remainingTime);
      return `${sec}s`;
    } else if (remainingTime < 600) {
      // less than 10m
      // time format example in this condition: 9m 58s
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      const sec = Math.floor(remainingTime - min * 60);
      return `${min}m ${sec}s`;
    } else if (remainingTime >= 600 && remainingTime < 3600) {
      // larger than 10m , less than 1hr
      // time format: 45m
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      return `${min}m`;
    }
  } else if (isNumber) {
    if (remainingTime <= 0) {
      // event already started
      // time format example: -5m
      const currentTimeLocal = moment(Date.now());
      const min = currentTimeLocal.diff(eventStartsAt, 'minutes');
      return `-${min}m`;
    } else if (remainingTime < 60) {
      // less than 60s
      // time format examples in this condition: 58s
      const sec = Math.floor(remainingTime);
      return sec + 's';
    } else if (remainingTime < 600) {
      // less than 10m
      // time format example in this condition: 9m 58s
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      const sec = Math.floor(remainingTime - min * 60);
      return `${min}m ${sec}s`;
    } else if (remainingTime < 3600) {
      // less than 1hr
      // time format examples in this condition: 26m
      const min = Math.floor(moment.unix(remainingTime).format('m'));
      return `${min}m`;
    }
  }
}
